import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import type { SourceFragment } from "../../shared/api/kdSourceTypes";
import { Button, Callout, Spinner } from "../../shared/ui/Primitives";

export function KDSourceFragment({ versionId, locator, reference }: {
  versionId: string; locator: string; reference: string;
}) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [fragment, setFragment] = useState<SourceFragment | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  async function load() {
    setLoading(true); setError("");
    try { setFragment(await api.getKDSourceFragment(versionId, locator, reference)); }
    catch (e) { setError(e instanceof Error ? e.message : "Не удалось открыть фрагмент"); }
    finally { setLoading(false); }
  }
  async function download(fileId: string) {
    setDownloading(true); setError("");
    try {
      const file = await api.downloadKDSourceFile(versionId, fileId);
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement("a"); anchor.href = url; anchor.download = file.filename || "source"; anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e instanceof Error ? e.message : "Не удалось скачать оригинал"); }
    finally { setDownloading(false); }
  }
  return <div className="kd-source-fragment-control">
    <Button variant="secondary" size="small" aria-expanded={open} onClick={() => {
      setOpen(!open); if (!open && !fragment && !loading) void load();
    }}>{open ? "Закрыть фрагмент" : "Открыть фрагмент"}</Button>
    {open && <section className="kd-source-fragment stack" aria-label="Фрагмент источника">
      {loading && <Spinner label="Загружаем фрагмент" />}
      {error && <Callout tone="warning">{error}</Callout>}
      {!loading && !fragment && error && <Button variant="secondary" size="small" onClick={() => void load()}>Повторить загрузку</Button>}
      {fragment && <>
        <strong>{fragment.title} · версия {fragment.revision}</strong>
        <span>{({ draft: "Черновик", active: "Подтверждён", retired: "Выведен из использования" })[fragment.status]} · {fragment.reference}</span>
        {fragment.total_matches > 1 && <p>В этой версии найдено фрагментов с таким адресом и ссылкой: {fragment.total_matches}. Проверьте имя файла.</p>}
        {fragment.limited && <Callout tone="warning">Показаны первые пять совпадений. Остальные доступны в оригиналах.</Callout>}
        {fragment.matches.map((match, i) => <div className="stack" key={`${match.file.id}:${i}`}>
          <strong>{match.file.filename}</strong>
          {match.segments.map(part => <div className={part.is_match ? "kd-source-fragment__match" : "kd-source-fragment__context"} key={part.segment_index}>
            <p><strong>{part.is_match ? "Найденное упоминание" : "Соседний контекст"} · {part.locator}</strong></p>
            {part.is_match && part.heading && <p>Раздел: {part.heading}</p>}
            <pre className="kd-normative-excerpt">{part.text}</pre>
          </div>)}
          <Button variant="secondary" size="small" disabled={downloading} onClick={() => void download(match.file.id)}>Скачать оригинал</Button>
        </div>)}
      </>}
    </section>}
  </div>;
}
